import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AsyncStorageHelper from '../services/asyncStorageHelper';

const PWAInstallMessage = () => {
  const [showInstallMessage, setShowInstallMessage] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const checkInstallability = async () => {
      // Check if the app is already installed
      const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
      if (isInstalled) return;

      // Check if user has dismissed the message permanently
      const dismissed = await AsyncStorage.getItem('pwaInstallDismissed');
      if (dismissed === 'true') return;

      // Check if user selected "Later" and if delay has passed
      const laterTimestamp = await AsyncStorage.getItem('pwaInstallLater');
      if (laterTimestamp) {
        const laterTime = parseInt(laterTimestamp, 10);
        const now = Date.now();
        const dayInMs = 24 * 60 * 60 * 1000; // 24 hours
        
        if (now - laterTime < dayInMs) {
          // Still within "Later" period, don't show
          return;
        } else {
          // "Later" period has expired, remove the timestamp
          await AsyncStorage.removeItem('pwaInstallLater');
        }
      }

      // Better iOS detection
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOSSafari = isIOS && isSafari;

      // Check for PWA support in manifest
      const hasManifest = document.querySelector('link[rel="manifest"]');
      
      if (isIOSSafari && hasManifest) {
        // Only show iOS instructions for actual iOS Safari
        setShowInstallMessage(true);
      } else if (!isIOSSafari && hasManifest && 'serviceWorker' in navigator) {
        // For other browsers, wait for beforeinstallprompt or show after delay
        setTimeout(() => {
          if (!deferredPrompt) {
            // Some browsers support PWA but don't fire beforeinstallprompt
            setShowInstallMessage(true);
          }
        }, 2000);
      }
    };

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallMessage(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    checkInstallability();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;

    if (deferredPrompt) {
      // Use the native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowInstallMessage(false);
    } else if (isIOSSafari) {
      // Show iOS Safari specific instructions
      alert('To install this app:\n1. Tap the Share button\n2. Tap "Add to Home Screen"');
    } else {
      // For other browsers, provide generic instructions
      alert('To install this app, look for "Install" or "Add to Home Screen" option in your browser menu.');
    }
  };

  const handleLater = async () => {
    try {
      const success = await AsyncStorageHelper.setItem('pwaInstallLater', Date.now().toString());
      if (success) {
        setShowInstallMessage(false);
        console.log('PWA install reminder set for 24 hours later');
      } else {
        console.error('Failed to save PWA install later timestamp');
      }
    } catch (error) {
      console.error('Error setting PWA install later:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      const success = await AsyncStorageHelper.setItem('pwaInstallDismissed', true);
      if (success) {
        setShowInstallMessage(false);
        console.log('PWA install permanently dismissed');
      } else {
        console.error('Failed to save PWA install dismissal');
      }
    } catch (error) {
      console.error('Error dismissing PWA install message:', error);
    }
  };

  if (!showInstallMessage || Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Ionicons name="download-outline" size={20} color="#0066cc" />
        <View style={styles.textContainer}>
          <Text style={styles.title}>Install App</Text>
          <Text style={styles.message}>
            Add Trials Live Scoring to your home screen for quick access
          </Text>
        </View>
        <View style={styles.buttons}>
          <TouchableOpacity style={styles.installButton} onPress={handleInstallClick}>
            <Text style={styles.installButtonText}>Install</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.laterButton} onPress={handleLater}>
            <Text style={styles.laterButtonText}>Later</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.dismissButton} onPress={handleDismiss}>
            <Ionicons name="close" size={20} color="#666" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 1000,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  textContainer: {
    flex: 1,
    marginLeft: 12,
    marginRight: 16,
  },
  title: {
    fontSize: 16,
    
    color: '#333',
    fontFamily: 'Montserrat_600SemiBold',
    marginBottom: 2,
  },
  message: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
    lineHeight: 18,
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  installButton: {
    backgroundColor: '#0066cc',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 8,
  },
  installButtonText: {
    color: '#fff',
    fontSize: 14,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  laterButton: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  laterButtonText: {
    color: '#666',
    fontSize: 14,
    
    fontFamily: 'Montserrat_500Medium',
  },
  dismissButton: {
    padding: 4,
  },
});

export default PWAInstallMessage;